// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, Input, OnInit } from '@angular/core';
import { downloadCSVFile } from '@lfx/core/helper';
import { Company, EmployeeMeetingStats } from '@lfx/core/models';
import { CompanyService } from '@lfx/core/services';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'lfx-profile-snapshot-employee-meetings',
  templateUrl: './profile-snapshot-employee-meetings.component.html',
  styleUrls: ['./profile-snapshot-employee-meetings.component.scss'],
  standalone: false,
})
export class ProfileSnapshotEmployeeMeetingsComponent implements OnInit {
  @Input() timeRange$: Observable<string>;
  @Input() userId: string;
  searchValue = '';
  _searchValue = new BehaviorSubject<string>('');
  searchValue$ = this._searchValue.asObservable();
  collectionSize = 0;
  currentPage = 1;
  pageSize = 10;
  _page = new BehaviorSubject<number>(1);
  page$ = this._page.asObservable();
  listData$: Observable<any[]>;
  pageData$: Observable<any[]>;
  isLoading = true;
  myCompany$: Observable<Company>;
  columns = [
    {
      name: 'Project Name',
      prop: 'projectName',
      clickableText: false,
      linkableText: false,
      dataStyle: {
        fontWeight: '400',
        fontSize: '14px',
        // color: '#009ADE',
      },
    },
    {
      name: 'Committee',
      prop: 'committeeType',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
    {
      name: 'Attendance',
      prop: 'attendance',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
  ];

  constructor(private companyService: CompanyService) {
    this.myCompany$ = this.companyService.getMyCompany();
  }

  ngOnInit(): void {
    this.listData$ = combineLatest([
      this.myCompany$,
      this.timeRange$.pipe(
        tap(() => {
          this._page.next(1);
          this.currentPage = 1;
        })
      ),
      this.searchValue$,
    ]).pipe(
      tap(() => {
        this.isLoading = true;
      }),
      switchMap(([company, timeRange, searchValue]) =>
        this.companyService
          .getEmployeeMeetings(
            this.userId,
            company.id,
            this.companyService.isAllOrgsRollup(),
            timeRange
          )
          .pipe(
            map(data => this.transformData(data)),
            map(data => this.filterData(data, searchValue)),
            tap(data => {
              this.collectionSize = data.length;
            })
          )
      ),
      tap(() => (this.isLoading = false))
    );

    this.pageData$ = combineLatest([this.listData$, this.page$]).pipe(
      map(([data, page]) => {
        const startIndex = (page - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;

        return data.slice(startIndex, endIndex);
      })
    );
  }

  private transformData(data: EmployeeMeetingStats[]): any[] {
    return data.map(item => ({
      attendedMeetingCount: item.attendedMeetingCount,
      committeeType: this.formatText(item.committeeType),
      meetingCount: item.meetingCount,
      projectName: this.formatText(item.projectName),
      attendance: this.formatText(this.prepareAttendanceText(item)),
    }));
  }

  prepareAttendanceText(item: any) {
    return `${item.attendedMeetingCount}/${item.meetingCount} (${Math.ceil(
      (+item.attendedMeetingCount / +item.meetingCount) * 100
    )}%)`;
  }

  formatText(text, subtext = null, url = null) {
    return {
      text,
      subtext,
      url,
    };
  }

  onPageChange(event: any) {
    this._page.next(event);
  }

  private filterData(data: any[], searchValue: string): any[] {
    return data.filter(
      i =>
        i.projectName.text.toLowerCase().includes(searchValue.toLowerCase()) ||
        i.committeeType.text.toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  clearSearch() {
    this.searchValue = '';
    this.searchInputChange('');
  }

  searchInputChange(value: string): void {
    this._page.next(1);
    this.currentPage = 1;
    this._searchValue.next(value);
  }

  exportData() {
    const subscription = this.listData$.subscribe(data => {
      downloadCSVFile(
        data.map(item => ({
          'Project Name': item.projectName.text,
          'Committee Type': item.committeeType.text,
          'Meetings Count': item.meetingCount,
          'Attended Meetings Count': item.attendedMeetingCount,
          Attendance: this.prepareAttendanceText(item),
        })),
        'Employee Meetings'
      );
      subscription.unsubscribe();
    });
  }
}
